/**
 * AI-client integration catalog (`/integraciones/:slug`).
 *
 * Every client connects through the SAME Zettapost MCP connector
 * (`MCP_URL`, OAuth + dynamic client registration — no tokens). What changes
 * per client is the headline, the connection steps (each app adds an MCP
 * server in a different place) and the SEO keyword. Prompts + capabilities are
 * shared because they describe what Zettapost exposes over MCP, not the client.
 *
 * ChatGPT lives on its own page (`/chatgpt`) and is intentionally NOT in this
 * catalog to avoid duplicate content; the hub links to it directly.
 *
 * Keep slugs in sync with `server/src/seo/integrationsData.ts`.
 */
import type { LucideIcon } from 'lucide-react';
import { Bot, MousePointer2, Sparkles, Terminal, Code2, Send, CalendarClock, BarChart3, MessageSquareText } from 'lucide-react';

export const MCP_URL = 'https://zettapost.com/mcp';

export type Step = { title: string; body: string };

export type Integration = {
  slug: string;
  name: string;
  icon: LucideIcon;
  /** Hero accent, e.g. "hablando con Claude". */
  headlineAccent: string;
  subhead: string;
  shortDescription: string;
  /** Long-form crawlable intro paragraph. */
  intro: string;
  steps: Step[];
  faq: { q: string; a: string }[];
};

/** Shared connection steps tail (identical across clients). */
const COMMON_TAIL: Step[] = [
  {
    title: 'Registro automático (DCR)',
    body: 'No necesitas crear ni pegar ningún token ni clave: el conector se registra solo mediante Dynamic Client Registration.',
  },
  {
    title: 'Inicia sesión y autoriza',
    body: 'Se abrirá el login de Zettapost. Inicia sesión y pulsa “Autorizar”. Listo: tu IA ya puede gestionar tus redes.',
  },
];

const COMMON_FAQ = (name: string) => [
  { q: `¿Necesito saber programar para conectar ${name} con Zettapost?`, a: 'No. Solo pegas la URL del conector y autorizas con tu cuenta. No hay que tocar código ni copiar tokens.' },
  { q: '¿Es seguro?', a: 'Sí. La conexión usa OAuth: inicias sesión en Zettapost y concedes el acceso, que puedes revocar cuando quieras. Nunca compartes contraseñas ni claves.' },
  { q: '¿En qué planes está incluido?', a: 'El conector MCP está incluido en todos los planes activos de Zettapost, sin coste adicional.' },
];

export const INTEGRATIONS: Integration[] = [
  {
    slug: 'claude',
    name: 'Claude',
    icon: MessageSquareText,
    headlineAccent: 'hablando con Claude',
    subhead:
      'Conecta Zettapost a Claude (Desktop, web o Claude Code) y publica, programa y mide tus redes sociales con lenguaje natural. Tú lo pides, Claude lo hace en tu cuenta.',
    shortDescription: 'Publica, programa y analiza tus redes desde Claude con el conector MCP.',
    intro:
      'Claude admite conectores MCP, así que puedes darle acceso —con tu permiso— a tu cuenta de Zettapost y gestionar tus redes sociales sin salir de la conversación. En lugar de abrir el panel, le pides a Claude que publique, programe o te resuma tus métricas y lo hace por ti. La conexión usa OAuth: inicias sesión y autorizas, sin copiar ni pegar claves.',
    steps: [
      {
        title: 'Abre los conectores en Claude',
        body: 'En Claude Desktop o en claude.ai ve a Ajustes → Conectores → “Añadir conector personalizado”. En Claude Code añade el servidor MCP desde tu configuración de MCP.',
      },
      {
        title: 'Pega la URL del servidor',
        body: `Usa ${MCP_URL} como URL del servidor MCP (transporte HTTP) y elige autenticación OAuth.`,
      },
      ...COMMON_TAIL,
    ],
    faq: COMMON_FAQ('Claude'),
  },
  {
    slug: 'cursor',
    name: 'Cursor',
    icon: MousePointer2,
    headlineAccent: 'desde Cursor',
    subhead:
      'Conecta Zettapost a Cursor con el conector MCP y gestiona tus redes sin salir del editor: publica, programa y consulta analíticas hablando con la IA.',
    shortDescription: 'Gestiona tus redes desde Cursor con el conector MCP, sin salir del editor.',
    intro:
      'Cursor soporta servidores MCP, así que puedes conectar tu cuenta de Zettapost y pedirle a la IA del editor que publique o programe contenido mientras trabajas. Ideal si vives en el editor y quieres disparar publicaciones o revisar métricas sin cambiar de herramienta. La conexión es por OAuth, sin tokens.',
    steps: [
      {
        title: 'Abre los ajustes de MCP en Cursor',
        body: 'Ve a Cursor → Settings → MCP (Tools & Integrations) → “Add new MCP server”. También puedes editar el archivo ~/.cursor/mcp.json.',
      },
      {
        title: 'Añade el servidor',
        body: `Tipo HTTP, URL ${MCP_URL}, autenticación OAuth.`,
      },
      ...COMMON_TAIL,
    ],
    faq: COMMON_FAQ('Cursor'),
  },
  {
    slug: 'gemini',
    name: 'Gemini',
    icon: Sparkles,
    headlineAccent: 'con Gemini',
    subhead:
      'Conecta Zettapost a Gemini (Gemini CLI) mediante MCP y gestiona tus redes con lenguaje natural: publica, programa y mide, sin pegar tokens.',
    shortDescription: 'Conecta tus redes a Gemini con el conector MCP de Zettapost.',
    intro:
      'Gemini CLI admite servidores MCP, lo que te permite conectar tu cuenta de Zettapost y gestionar tus redes hablando con Gemini. Le pides que publique, programe o te resuma analíticas y lo ejecuta en tu cuenta. La autenticación es OAuth, sin necesidad de claves.',
    steps: [
      {
        title: 'Añade el servidor MCP en Gemini',
        body: 'Edita tu configuración de MCP de Gemini CLI (sección mcpServers en ~/.gemini/settings.json) y añade Zettapost como servidor HTTP.',
      },
      {
        title: 'Pega la URL del servidor',
        body: `Usa ${MCP_URL} con autenticación OAuth.`,
      },
      ...COMMON_TAIL,
    ],
    faq: COMMON_FAQ('Gemini'),
  },
  {
    slug: 'codex',
    name: 'Codex',
    icon: Terminal,
    headlineAccent: 'desde Codex',
    subhead:
      'Conecta Zettapost a Codex (OpenAI) con el conector MCP y publica, programa o consulta tus redes desde la terminal hablando con la IA.',
    shortDescription: 'Maneja tus redes desde Codex (OpenAI) con el conector MCP.',
    intro:
      'Codex, el agente de OpenAI, admite servidores MCP. Conectando tu cuenta de Zettapost puedes pedirle que publique o programe contenido y consulte tus métricas sin salir de la terminal. La conexión usa OAuth, sin pegar tokens.',
    steps: [
      {
        title: 'Añade el servidor MCP en Codex',
        body: 'Edita la configuración de MCP de Codex (sección [mcp_servers] en ~/.codex/config.toml) y añade Zettapost con la URL del servidor.',
      },
      {
        title: 'Pega la URL del servidor',
        body: `Usa ${MCP_URL} con transporte HTTP y autenticación OAuth.`,
      },
      ...COMMON_TAIL,
    ],
    faq: COMMON_FAQ('Codex'),
  },
  {
    slug: 'vscode',
    name: 'VS Code',
    icon: Code2,
    headlineAccent: 'desde VS Code',
    subhead:
      'Conecta Zettapost a VS Code (GitHub Copilot en modo agente) con el conector MCP y gestiona tus redes sin salir del editor.',
    shortDescription: 'Conecta tus redes a VS Code (Copilot) con el conector MCP.',
    intro:
      'VS Code permite añadir servidores MCP para usarlos con GitHub Copilot en modo agente. Conectando Zettapost puedes pedirle que publique, programe o te resuma analíticas mientras programas. La conexión es por OAuth, sin claves manuales.',
    steps: [
      {
        title: 'Añade el servidor MCP en VS Code',
        body: 'Con Copilot en modo agente, abre la paleta de comandos → “MCP: Add Server” → HTTP. También puedes crear un archivo .vscode/mcp.json.',
      },
      {
        title: 'Pega la URL del servidor',
        body: `Usa ${MCP_URL} con autenticación OAuth.`,
      },
      ...COMMON_TAIL,
    ],
    faq: COMMON_FAQ('VS Code'),
  },
];

export function getIntegrationBySlug(slug: string): Integration | undefined {
  return INTEGRATIONS.find((i) => i.slug === slug);
}

/* ── Shared content (what Zettapost exposes over MCP) ───────── */

export const CAPABILITIES = [
  'Publicar de inmediato en cualquier red conectada',
  'Programar publicaciones para una fecha y hora',
  'Adaptar el texto por red social',
  'Consultar analíticas y mejores publicaciones',
  'Ver tus cuentas conectadas',
  'Comprobar el estado de una publicación',
];

export const PROMPTS: { icon: LucideIcon; label: string; prompt: string }[] = [
  { icon: Send, label: 'Publicar al instante', prompt: 'Publica ahora en Instagram y X: «Nuevo artículo en el blog 🎉 Cómo programar tus redes en 5 minutos». Añade la imagen https://…/portada.jpg' },
  { icon: CalendarClock, label: 'Programar', prompt: 'Programa este post en LinkedIn para el viernes a las 10:00 (hora de Madrid): «3 errores que cometen las marcas en redes…».' },
  { icon: BarChart3, label: 'Analíticas', prompt: '¿Cómo han ido mis redes los últimos 30 días? Dame likes, alcance e interacción y mis 3 mejores publicaciones.' },
  { icon: MessageSquareText, label: 'Texto por red', prompt: 'Coge esta idea y adáptala: versión con emojis y hashtags para Instagram, más profesional para LinkedIn, y prográmalas mañana.' },
  { icon: Bot, label: 'Tus cuentas', prompt: '¿Qué cuentas de redes tengo conectadas en Zettapost?' },
  { icon: Sparkles, label: 'Plan de contenido', prompt: 'Proponme 5 publicaciones para esta semana sobre productividad y prográmalas en Instagram repartidas de lunes a viernes.' },
];
